import React, { useEffect, useState, useCallback } from "react";
import {
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  BarChart, Bar,
  PieChart, Pie, Cell
} from "recharts";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../pagini/Firebase";
import "./Stiluri/RaportSocial.css";

const culoriPie = ["#007bff", "#28a745", "#ffc107", "#dc3545", "#6f42c1", "#fd7e14"];
const judeteRO = [
  "Alba", "Arad", "Argeș", "Bacău", "Bihor", "Bistrița-Năsăud", "Botoșani", "Brașov",
  "Brăila", "Buzău", "Caraș-Severin", "Călărași", "Cluj", "Constanța", "Covasna", "Dâmbovița",
  "Dolj", "Galați", "Giurgiu", "Gorj", "Harghita", "Hunedoara", "Ialomița", "Iași", "Ilfov",
  "Maramureș", "Mehedinți", "Mureș", "Neamț", "Olt", "Prahova", "Satu Mare", "Sălaj", "Sibiu",
  "Suceava", "Teleorman", "Timiș", "Tulcea", "Vaslui", "Vâlcea", "Vrancea",
  "Sector 1", "Sector 2", "Sector 3", "Sector 4", "Sector 5", "Sector 6"
];

// Helper format date for input[type=date]
const formatDateForInput = (date) => date.toISOString().split("T")[0];

// Helper format date and time for display in table
const formatDateAfisare = (date) => {
  if (!date) return "";
  return (
    date.toLocaleDateString("ro-RO", { year: "numeric", month: "2-digit", day: "2-digit" }) +
    " " +
    date.toLocaleTimeString("ro-RO", { hour: "2-digit", minute: "2-digit" })
  );
};

const RaportSocial = () => {
  // Liste și filtre
  const [judetSelectat, setJudetSelectat] = useState("");

  const [perioadaSelectata, setPerioadaSelectata] = useState("Ultimele 7 zile");
  const [dataStart, setDataStart] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 7);
    return formatDateForInput(d);
  });
  const [dataEnd, setDataEnd] = useState(() => formatDateForInput(new Date()));

  // Date rapoarte
  const [beneficiariPerJudet, setBeneficiariPerJudet] = useState([]);
  const [varstePerJudet, setVarstePerJudet] = useState([]);
  const [sexPerJudet, setSexPerJudet] = useState([]);
  const [cazuriNoi, setCazuriNoi] = useState(0);
  const [totalBeneficiari, setTotalBeneficiari] = useState(0);

  // Beneficiari pentru tabel
  const [beneficiariInInterval, setBeneficiariInInterval] = useState([]);

  // Pagina curentă pentru tabel
  const [paginaCurenta, setPaginaCurenta] = useState(1);
  const paginePePagina = 5;

  // Reset pagină la schimbarea filtrelor
  useEffect(() => {
    setPaginaCurenta(1);
  }, [judetSelectat, perioadaSelectata, dataStart, dataEnd]);

  // Memorează funcția calculeazaInterval cu useCallback
  const calculeazaInterval = useCallback(() => {
    const azi = new Date();
    let start, end;

    if (perioadaSelectata === "Complet") {
      return { start: null, end: null };
    }

    end = new Date(azi);
    switch (perioadaSelectata) {
      case "Astăzi":
        start = new Date(azi);
        break;
      case "Ieri":
        start = new Date(azi);
        start.setDate(start.getDate() - 1);
        end = new Date(start);
        break;
      case "Ultimele 7 zile":
        start = new Date(azi);
        start.setDate(start.getDate() - 7);
        break;
      case "Ultimele 30 zile":
        start = new Date(azi);
        start.setDate(start.getDate() - 30);
        break;
      case "Ultimele 90 zile":
        start = new Date(azi);
        start.setDate(start.getDate() - 90);
        break;
      case "Ultimele 365 zile":
        start = new Date(azi);
        start.setDate(start.getDate() - 365);
        break;
      case "Interval personalizat":
        start = new Date(dataStart);
        end = new Date(dataEnd);
        break;
      default:
        start = new Date(azi);
        start.setDate(start.getDate() - 7);
    }
    start.setHours(0, 0, 0, 0);
    end.setHours(23, 59, 59, 999);
    return { start, end };
  }, [perioadaSelectata, dataStart, dataEnd]);

  // Fetch și calcul date la schimbarea filtrelor județ și perioadă
  useEffect(() => {
    async function fetchData() {
      const { start, end } = calculeazaInterval();

      const snapshot = await getDocs(collection(db, "Beneficiari"));
      const allBeneficiari = snapshot.docs.map(doc => {
        const d = doc.data();
        return {
          id: doc.id,
          nume: d.nume || "",
          prenume: d.prenume || "",
          cnp: d.cnp || "",
    judet: d.JudetSector || d.locatie?.judet || d.ultimaLocatie?.judet || "Necunoscut",
          varsta: calcVarsta(d.data_nasterii),
          sex: d.Sex || d.gen || "Necunoscut",
          stareCivila: d.stare_civila || "Necunoscut",
          educatie: d.educatie || "Necunoscut",
          ocupatie: d.ocupatie || "Necunoscut",
          dataAdaugare: d.dataAdaugare ? new Date(d.dataAdaugare.seconds * 1000) : null,
          servicii: d.servicii || [],
          dosareActive: d.dosareActive || [],
          dosarComplet: d.dosarComplet || false,
          adaugatDe: d.adaugatDe || "",
          institutie: d.institutie || "",
        };
      });

      // Set lista de județe/sectoare disponibile (dinamic)
      // const judeteUnice = Array.from(new Set(allBeneficiari.map(b => b.judet))).sort();
      // setListaJudetSector(judeteUnice);

      // Filtrare beneficiari după județ și perioada selectată

      const beneficiariFiltrati = allBeneficiari.filter(b => {
        const condJudet = b.judet === judetSelectat;

        if (perioadaSelectata === "Complet") {
          return condJudet;
        }

        const condPerioada = b.dataAdaugare
          ? (start && end ? (b.dataAdaugare >= start && b.dataAdaugare <= end) : true)
          : false;

        return condJudet && condPerioada;
      });

      // Sortare corectă cu getTime
      const beneficiariSortati = [...beneficiariFiltrati].sort((a, b) => {
        const timeA = a.dataAdaugare instanceof Date ? a.dataAdaugare.getTime() : 0;
        const timeB = b.dataAdaugare instanceof Date ? b.dataAdaugare.getTime() : 0;
        return timeB - timeA;
      });

      // Calcul număr total beneficiari per județ pentru grafic
      const countPerJudet = {};
      beneficiariSortati.forEach(b => {
        countPerJudet[b.judet] = (countPerJudet[b.judet] || 0) + 1;
      });
      setBeneficiariPerJudet(
        Object.entries(countPerJudet).map(([judet, count]) => ({ judet, count }))
      );

      // Distribuție pe categorii de vârstă
      const bucketsVarsta = { "0-18": 0, "19-35": 0, "36-60": 0, "60+": 0 };
      beneficiariSortati.forEach(b => {
        if (b.varsta !== null) {
          if (b.varsta <= 18) bucketsVarsta["0-18"]++;
          else if (b.varsta <= 35) bucketsVarsta["19-35"]++;
          else if (b.varsta <= 60) bucketsVarsta["36-60"]++;
          else bucketsVarsta["60+"]++;
        }
      });
      setVarstePerJudet(
        Object.entries(bucketsVarsta).map(([range, count]) => ({ range, count }))
      );

      // Distribuție pe sex
      const sexCount = {};
      beneficiariSortati.forEach(b => {
        sexCount[b.sex] = (sexCount[b.sex] || 0) + 1;
      });
      setSexPerJudet(
        Object.entries(sexCount).map(([sex, count]) => ({ sex, count }))
      );

      // Număr cazuri noi în perioada
      setCazuriNoi(beneficiariSortati.length);

      // Set lista pentru tabel
      setBeneficiariInInterval(beneficiariSortati);
    }

    fetchData();
  }, [judetSelectat, perioadaSelectata, dataStart, dataEnd, calculeazaInterval, ]);

  
  useEffect(() => {
  const total = beneficiariPerJudet
    .filter(b => b.judet === judetSelectat)
    .reduce((sum, b) => sum + b.count, 0);
  setTotalBeneficiari(total);
}, [judetSelectat, beneficiariPerJudet]);

  // Calcul vârstă simplu
  function calcVarsta(dataNasterii) {
    if (!dataNasterii) return null;
    const dn = new Date(dataNasterii);
    const azi = new Date();
    let varsta = azi.getFullYear() - dn.getFullYear();
    if (
      azi.getMonth() < dn.getMonth() ||
      (azi.getMonth() === dn.getMonth() && azi.getDate() < dn.getDate())
    ) {
      varsta--;
    }
    return varsta;
  }

  // Beneficiari pentru pagina curentă
  const indexUltimBeneficiar = paginaCurenta * paginePePagina;
  const indexPrimulBeneficiar = indexUltimBeneficiar - paginePePagina;
  const beneficiariPagina = beneficiariInInterval.slice(indexPrimulBeneficiar, indexUltimBeneficiar);

  // Total pagini
  const totalPagini = Math.ceil(beneficiariInInterval.length / paginePePagina);

  return (
    <div className="dashboard-container-rapoarte">
      <div className="raport-intro">
        <h1 className="raport-intro-titlu">Date statistice și indicatori locali</h1>
        <p className="raport-intro-subtitlu">
          Pentru a obține date la nivel local, vă rugăm să selectați județul aferent din meniul drop-down
        </p>
      </div>

      {/* Filtru județ/sector */}
<div className="filtru-judet" style={{ marginBottom: "1rem" }}>
  <label htmlFor="selectJudet" style={{ marginRight: "0.5rem" }}>
    Selectează județ/sector:
  </label>
<select
  id="selectJudet"
  value={judetSelectat}
  onChange={(e) => setJudetSelectat(e.target.value)}
>
  <option value="">-- Selectează județ/sector --</option>
{judeteRO.map(judet => (
  <option key={judet} value={judet}>{judet}</option>
))}

</select>


</div>

      {/* Filtru perioadă */}
      <div className="filtru-perioada" style={{ marginBottom: "1rem" }}>
        <label htmlFor="selectPerioada" style={{ marginRight: "0.5rem" }}>Selectează perioada:</label>
        <select
          id="selectPerioada"
          value={perioadaSelectata}
          onChange={(e) => setPerioadaSelectata(e.target.value)}
        >
          <option>Complet</option>
          <option>Astăzi</option>
          <option>Ieri</option>
          <option>Ultimele 7 zile</option>
          <option>Ultimele 30 zile</option>
          <option>Ultimele 90 zile</option>
          <option>Ultimele 365 zile</option>
          <option>Interval personalizat</option>
        </select>
      </div>

      {/* Inputuri date dacă se alege interval personalizat */}
      {perioadaSelectata === "Interval personalizat" && (
        <div className="filtru-date-personalizate" style={{ marginBottom: "1rem" }}>
          <label htmlFor="dataStart" style={{ marginRight: "0.5rem" }}>Data start:</label>
          <input
            type="date"
            id="dataStart"
            value={dataStart}
            max={dataEnd}
            onChange={(e) => setDataStart(e.target.value)}
          />
          <label htmlFor="dataEnd" style={{ margin: "0 0.5rem 0 1rem" }}>Data finală:</label>
          <input
            type="date"
            id="dataEnd"
            value={dataEnd}
            min={dataStart}
            max={formatDateForInput(new Date())}
            onChange={(e) => setDataEnd(e.target.value)}
          />
        </div>
      )}

      {/* Grafice */}
      <section className="kpi-cards">
        <div className="kpi-card">
          <h3>Total Beneficiari</h3>
          <p>{totalBeneficiari}</p>
        </div>
        <div
          className="kpi-card"
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <h3>Beneficiari noi/interval</h3>
          <p style={{ fontSize: "2rem", fontWeight: "bold", textAlign: "center" }}>{cazuriNoi}</p>
        </div>
      </section>

      <section className="grafice-rapoarte" style={{ display: "flex", flexWrap: "wrap", gap: "2rem" }}>


        <div className="chart-card" style={{ flex: "1 1 500px" }}>
          <h3>Distribuție pe categorii de vârstă</h3>
          <BarChart width={500} height={300} data={varstePerJudet}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="range" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Bar dataKey="count" fill="#28a745" />
          </BarChart>
        </div>

        <div className="chart-card" style={{ flex: "1 1 400px" }}>
          <h3>Distribuție pe sex</h3>
          <PieChart width={400} height={300}>
            <Pie
              data={sexPerJudet}
              dataKey="count"
              nameKey="sex"
              cx="50%"
              cy="50%"
              outerRadius={100}
              fill="#8884d8"
              label
            >
              {sexPerJudet.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={culoriPie[index % culoriPie.length]} />
              ))}
            </Pie>
            <Tooltip />
            <Legend />
          </PieChart>
        </div>


      </section>

      {/* Tabel beneficiari */}
      <section className="tabel-rapoarte-social-beneficiari" style={{ marginTop: "3rem" }}>
        <h3>Beneficiari adăugați în perioada selectată</h3>
        <table
          className="tabel-raport-social"
          style={{
            width: "100%",
            borderCollapse: "collapse",
            border: "1px solid #ddd",
          }}
        >
          <thead>
            <tr style={{ backgroundColor: "#f2f2f2" }}>
              <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Nume Beneficiar
              </th>
              <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>CNP</th>
              <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Data Adăugării
              </th>
              <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Utilizator
              </th>
              <th style={{ padding: "8px", textAlign: "left", borderBottom: "1px solid #ddd" }}>
                Instituția
              </th>
            </tr>
          </thead>
          <tbody>
            {beneficiariPagina.length === 0 ? (
              <tr>
                <td colSpan={5} style={{ padding: "8px", textAlign: "center" }}>
                  Nu există beneficiari în această perioadă.
                </td>
              </tr>
            ) : (
              beneficiariPagina.map((b) => (
                <tr key={b.id} style={{ borderBottom: "1px solid #eee" }}>
                  <td style={{ padding: "8px" }}>{b.prenume} {b.nume}</td>
                  <td style={{ padding: "8px" }}>{b.cnp}</td>
                  <td style={{ padding: "8px" }}>{formatDateAfisare(b.dataAdaugare)}</td>
                  <td style={{ padding: "8px" }}>
                    {b.adaugatDe ? `${b.adaugatDe.prenume} ${b.adaugatDe.nume}` : ""}
                  </td>
                  <td style={{ padding: "8px" }}>{b.institutie}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="pagination" style={{ marginTop: "1rem", display: "flex", gap: "0.5rem" }}>
          {Array.from({ length: totalPagini }, (_, i) => (
            <button
              key={i}
              onClick={() => setPaginaCurenta(i + 1)}
              style={{
                padding: "0.5rem 1rem",
                backgroundColor: i + 1 === paginaCurenta ? "#007bff" : "#f0f0f0",
                color: i + 1 === paginaCurenta ? "#fff" : "#000",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </section>
    </div>
  );
};

export default RaportSocial;
